import React from 'react';
import PropTypes from 'prop-types';
import {
  compose,
  withStateHandlers,
  withProps,
  withHandlers,
  lifecycle,
} from 'recompose';
import AuthenticablePurchaseButton from './PurchaseButton';
import PromotionGroupOptions from './PromotionGroupOptions';
import PromotionCarousel from './PromotionCarousel';
import PromotionGroupDetail from './PromotionGroupDetail';
import PromotionGroupDiscountDesc from './PromotionGroupDiscountDesc';
import RedeemVoucher from './RedeemVoucher';
import PromotionGroupInfo from './PromotionGroupInfo';
import PromotionsCounter from './PromotionsCounter';
import BreadCrumb from './PromotionDynamicBreadCrumb';
import PromotionGroupPackage from './PromotionGroupPackage';
import { addPromotionToCart, addPromotionGroupToCart } from '../Common/Providers/PromotionApi';
import { showPermanentErrorMsg } from '../Common/ErrorMsg';
import { withDataLayerAddToCart } from '../Common/GTM/ECommerceTracking/withDataLayerAddToCart';
import { withDataLayerProductDetailView } from '../Common/GTM/ECommerceTracking/withDataLayerProductDetailView';

function PromotionGroupContainer({
  id,
  name,
  description,
  termsAndConditions,
  counter,
  timestamp,
  promotions,
  store,
  counterExpired,
  location,
  routes,
  onExpired,
  handlePromotionChange,
  selectedMedia,
  selectedPromotionId,
  purchasePromotion,
  purchasePromotionGroup,
  type,
  mediaUrl,
  totalSellingPrice,
  totalOriginalPrice,
  totalDiscountPercentage
}) {
  return (
    <div className='promotion-group'>
      <BreadCrumb
        name={name}
        routes={routes}
        path={location.pathname}
      />
      <div className="hline-bottom"></div>
      <div className='promotion-group__container container'>
        <div className='promotion-group__primary row no-gutters'>
          <div className='promotion-group__primary-left col-12 col-sm-7'>
            {type == 'standard' && <div className='promotion-group__carousel'>
              <PromotionCarousel
                media={selectedMedia()}
              />
            </div>}
            {type == 'package' && <img className='img-fluid' src={`${mediaUrl}`} />}
            <div className='promotion-group__detail promotion-group__detail--desktop'>
              <PromotionGroupDetail description={description} />
            </div>
          </div>
          <div className='promotion-group__primary-right col-12 col-sm-5'>
            <div className="promotion-group__information">
              <div className='promotion-group__name'>
                {name}
              </div>
              <PromotionGroupDiscountDesc promotions={promotions}/>
              <div className={`promotion-group__options ${counterExpired ? 'promotion-group__disabled' : ''}`}>
                {type == 'standard' && <PromotionGroupOptions
                  promotions={promotions}
                  handlePromotionChange={handlePromotionChange}
                  selectedPromotionId={selectedPromotionId}
                  store={store}
                  disabled={counterExpired}
                />}
                {type == 'package' && <PromotionGroupPackage
                  promotions={promotions}
                  store={store}
                  totalSellingPrice={totalSellingPrice}
                  totalOriginalPrice={totalOriginalPrice}
                  totalDiscountPercentage={totalDiscountPercentage}
                />}
              </div>
              <div className='promotion-group__main-action'>
                 <AuthenticablePurchaseButton
                  counterExpired={counterExpired}
                  purchasePromotion={type == 'standard' ? purchasePromotion : purchasePromotionGroup}
                  selectedPromotionId={type == 'standard' ?  selectedPromotionId : id} 
                />
              </div>
              <div className='promotion-group__timer'>
                <PromotionsCounter
                  timestamp={timestamp}
                  counter={counter}
                  onExpired={onExpired}/>
              </div>
              <div className='promotion-group__share'>
              </div>
              <div className='promotion-group__additional-info'>
                <PromotionGroupInfo
                  termsAndConditions={termsAndConditions}
                  id={id}
                  store={store}
                />
              </div>
            </div>
          </div>

          <div className='promotion-group__secondary row no-gutters mt-7 mx-auto'>
            <RedeemVoucher/>
          </div>
        </div>
      </div>
    </div>
  );
}

const enhance = compose(
  withStateHandlers(
    ({
      promotions,
      initialCounterExpired = false,
    }) => ({
      counterExpired: initialCounterExpired,
      selectedPromotionId: promotions[0].id,
    }),
    {
      onExpired: () => () => ({
        counterExpired: true,
      }),
      handlePromotionChange: () => value => ({
        selectedPromotionId: value,
      }),
    },
  ),
  withProps(({ promotions, selectedPromotionId }) => ({
    selectedMedia: () => {
      const option = promotions.find(e => (
        e.id === selectedPromotionId
      ));

      return option.media;
    },
  })),
  withDataLayerAddToCart,
  withHandlers({
    purchasePromotion: (props) => () => {
      addPromotionToCart(props.selectedPromotionId)
        .done((response) => {
          const promotion = props.promotions.find(promo => promo.id === props.selectedPromotionId);

          const data = {
            currencyCode: promotion.currency,
            product: {
              id: props.selectedPromotionId,
              name: props.slug,
              category: promotion.type,
              price: promotion.price.sellingPrice,
              variant: promotion.name,
              quantity: 1
            }
          }

          props.dataLayerAddToCart(data)
          window.location = response.data.redirect_to_cart_url;
        })
        .fail(() => {
          showPermanentErrorMsg("Error: Promotion is no longer available");
        });
    },
    purchasePromotionGroup: (props) => () => {
      addPromotionGroupToCart(props.id)
        .done((response) => {

          const data = {
            currencyCode: props.store.currency.code,
            product: {
              id: props.id,
              name: props.slug,
              category: props.type,
              price: props.totalSellingPrice,
              variant: props.name,
              quantity: 1
            }
          }

          props.dataLayerAddToCart(data)
          window.location = response.data.redirect_to_cart_url;
        })
        .fail(error => {
          showPermanentErrorMsg(error.responseJSON.errors || 'Error: Package is no longer available');
        });
    },
  }),
  withDataLayerProductDetailView,
  lifecycle({
    componentDidMount() {
      const promotionType = this.props.promotions.length > 0
        ? this.props.promotions[0].type
        : this.props.route.label.toLowerCase()

      this.props.dataLayerProductDetailView({
        id: this.props.id,
        name: this.props.slug,
        category: promotionType,
        currency: this.props.store.currency.code
      });
    }
  }),
);

PromotionGroupContainer.propTypes = {
  promotions: PropTypes.array,
};

export default enhance(PromotionGroupContainer);



// WEBPACK FOOTER //
// ./src/Promotion/PromotionGroupContainer.js